import { DateTime } from 'luxon';

import { ParseAny } from './dateparse';

const leadingWeekday = /^(?:(?:mon|tues|wednes|thurs|fri|satur|sun)day|(?:mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun)),?\s+/i;

export type ApproveByKind = 'empty' | 'invalid' | 'tooClose' | 'rush' | 'result';

export interface ApproveByDecision {
    kind: ApproveByKind;
    needed: DateTime | null;
    approveBy: DateTime | null;
    // Set only when the caller passes a rush lead. The standard approve-by
    // date is still `approveBy`.
    rushApproveBy: DateTime | null;
}

// September 30 of the federal fiscal year that contains today. October through
// December belong to the fiscal year that ends the following September.
export function FiscalYearEnd(today: DateTime): DateTime {
    const day = today.startOf('day');
    const year = day.month >= 10 ? day.year + 1 : day.year;
    return DateTime.fromObject({ year, month: 9, day: 30 }).startOf('day');
}

// The day the proof has to be approved so production and shipping finish on
// needed. A Saturday or Sunday moves back to Friday, because a weekend approval
// does not start production until Monday.
export function ApproveByDate(needed: DateTime, leadDays: number): DateTime {
    const approveBy = needed.startOf('day').minus({ days: leadDays });
    if (approveBy.weekday === 6) {
        return approveBy.minus({ days: 1 });
    }
    if (approveBy.weekday === 7) {
        return approveBy.minus({ days: 2 });
    }
    return approveBy;
}

// The calendar date a visitor typed, at local midnight. ParseAny owns the
// formats. A leading weekday is removed only when the whole string does not
// parse, because "Thursday, September 30, 2027" is not a form ParseAny accepts.
// A date typed without a year ("September 30", "9/30") is its next occurrence
// on or after today.
export function ParseNeededDate(input: string, today: DateTime = DateTime.now()): DateTime | null {
    const text = input.trim();
    if (text === '') {
        return null;
    }
    let parsed = firstDate(text);
    if (!parsed) {
        const stripped = text.replace(leadingWeekday, '');
        if (stripped === text) {
            return null;
        }
        parsed = firstDate(stripped);
    }
    if (parsed?.year === 0) {
        return nextOccurrence(parsed.month, parsed.day, today.startOf('day'));
    }
    return parsed;
}

// ParseAny reads a yearless date as year 0. February 29 skips to the next
// leap year.
function nextOccurrence(month: number, day: number, today: DateTime): DateTime | null {
    for (let year = today.year; year <= today.year + 4; year++) {
        const candidate = DateTime.fromObject({ year, month, day });
        if (candidate.isValid && candidate >= today) {
            return candidate.startOf('day');
        }
    }
    return null;
}

function firstDate(input: string): DateTime | null {
    const parsed = calendarDate(input);
    if (parsed) {
        return parsed;
    }
    const sept = withSept(input);
    if (sept === input) {
        return null;
    }
    return calendarDate(sept);
}

// "Sept" is the abbreviation people type. ParseAny accepts "Sep" and "Sept."
// and does not accept "Sept" before a space.
function withSept(input: string): string {
    return input.replace(/\bsept\b/gi, 'Sep');
}

// text is what the field shows. selected is the date the calculator is holding;
// a non-empty field uses it instead of parsing, so a locale's own long date is
// not rejected. An empty field is empty even when selected is set.
// rushLeadDays, when passed, is the shorter lead. The decision is `rush` when
// the standard approve-by date is before today and the rush approve-by date is
// today or later. Without it, a missed standard date stays `tooClose`.
export function ApproveByState(text: string, selected: DateTime | null, today: DateTime, leadDays: number, rushLeadDays?: number): ApproveByDecision {
    if (text.trim() === '') {
        return { kind: 'empty', needed: null, approveBy: null, rushApproveBy: null };
    }
    const needed = selected ?? ParseNeededDate(text, today);
    if (!needed) {
        return { kind: 'invalid', needed: null, approveBy: null, rushApproveBy: null };
    }
    const todayStart = today.startOf('day');
    const approveBy = ApproveByDate(needed, leadDays);
    const rushApproveBy = rushLeadDays === undefined ? null : ApproveByDate(needed, rushLeadDays);
    let kind: ApproveByKind = 'result';
    if (approveBy < todayStart) {
        if (rushApproveBy !== null && rushApproveBy >= todayStart) {
            kind = 'rush';
        } else {
            kind = 'tooClose';
        }
    }
    return { kind, needed, approveBy, rushApproveBy };
}

function calendarDate(input: string): DateTime | null {
    let parsed: DateTime;
    try {
        parsed = ParseAny(input, { zone: 'local' });
    } catch {
        return null;
    }
    if (!parsed.isValid) {
        return null;
    }
    const local = DateTime.fromObject({
        year: parsed.year,
        month: parsed.month,
        day: parsed.day,
    });
    if (!local.isValid) {
        return null;
    }
    return local.startOf('day');
}
